import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useSplashScreenController } from "@scenes/splash-screen/utils";
import { getUserDetails } from "@scenes/authentication/utils/utils";

const useUserAuthentication = (initialUrl?: string) => {
  const { hideSplashScreen } = useSplashScreenController();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state?.userInfo?.user);
  const userId = user?.id;
  const authToken = user?.token;
  const companyLocations = useSelector(
    (state: any) => state.userInfo.companyLocations
  );
  const hasTermsAccepted = useSelector(
    (state: any) => state.userInfo?.user?.accepted_terms
  );
  const isIFrame = useSelector(
    (state: any) => state.userInfo?.user?.iframe_status
  );
  const userCustomizations = useSelector(selectUserCustomizations);
  const appLoadStates = useSelector(selectAppLoadStates);

  const isReady =
    userId &&
    (hasTermsAccepted || isIFrame) &&
    (!userCustomizations.custom_theme || appLoadStates.isCompanyThemeLoaded) &&
    (!userCustomizations.json_navigation_menus ||
      appLoadStates.isNavigationCustomisationLoad) &&
    (!userCustomizations.pdps || appLoadStates.isPDPCustomisationLoad) &&
    (!userCustomizations.search_forms ||
      appLoadStates.isSearchFilterCustomisationLoad) &&
    (!userCustomizations.search_results ||
      appLoadStates.isSearchResultCustomisationLoad) &&
    !initialUrl?.includes("user/password");

  useEffect(() => {
    let timeout: number;
    if (isReady) {
      timeout = setTimeout(() => {
        hideSplashScreen();
        if (userId && authToken) {
          getUserDetails(userId, dispatch, companyLocations, authToken);
        }
      }, 200);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isReady, userId]);

  return isReady;
};

const selectUserInfo = (state: any) => state.userInfo?.user?.user;

const selectCustomTheme = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo?.custom_theme
);

const selectJsonNavigationMenus = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo?.json_navigation_menus
);

const selectPdps = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo?.pdps
);

const selectSearchForms = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo?.search_forms
);

const selectSearchResults = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo?.search_results
);

const selectUserCustomizations = createSelector(
  [
    selectCustomTheme,
    selectJsonNavigationMenus,
    selectPdps,
    selectSearchForms,
    selectSearchResults,
  ],
  (
    custom_theme,
    json_navigation_menus,
    pdps,
    search_forms,
    search_results
  ) => ({
    custom_theme,
    json_navigation_menus,
    pdps,
    search_forms,
    search_results,
  })
);

const selectAppState = (state: any) => state.appState;

const selectIsCompanyThemeLoaded = createSelector(
  [selectAppState],
  (appState) => appState?.isCompanyThemeLoaded
);

const selectIsNavigationCustomisationLoad = createSelector(
  [selectAppState],
  (appState) => appState?.isNavigationCustomisationLoad
);

const selectIsPDPCustomisationLoad = createSelector(
  [selectAppState],
  (appState) => appState?.isPDPCustomisationLoad
);

const selectIsSearchFilterCustomisationLoad = createSelector(
  [selectAppState],
  (appState) => appState?.isSearchFilterCustomisationLoad
);

const selectIsSearchResultCustomisationLoad = createSelector(
  [selectAppState],
  (appState) => appState?.isSearchResultCustomisationLoad
);

const selectAppLoadStates = createSelector(
  [
    selectIsCompanyThemeLoaded,
    selectIsNavigationCustomisationLoad,
    selectIsPDPCustomisationLoad,
    selectIsSearchFilterCustomisationLoad,
    selectIsSearchResultCustomisationLoad,
  ],
  (
    isCompanyThemeLoaded,
    isNavigationCustomisationLoad,
    isPDPCustomisationLoad,
    isSearchFilterCustomisationLoad,
    isSearchResultCustomisationLoad
  ) => ({
    isCompanyThemeLoaded,
    isNavigationCustomisationLoad,
    isPDPCustomisationLoad,
    isSearchFilterCustomisationLoad,
    isSearchResultCustomisationLoad,
  })
);

export default useUserAuthentication;
